import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  FileText,
  MessageCircle,
  Receipt,
  Upload,
  File,
  Trash2,
  Plus,
  Pencil,
  Save,
  X,
  AlertTriangle,
  CheckCircle2,
  Layers,
  Calendar,
  Settings,
  RefreshCw,
  Sparkles,
  Eye,
  Download,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { apiClient } from '../lib/apiClient';
import { formatDate, formatCurrency, formatFileSize, generateId } from '../lib/utils';
import { shareOfProjectValue } from '../lib/projectValue';
import { parseContractPdf, type ContractParseResult } from '../lib/contractParser';
import AiReviewModal from '../components/AiReviewModal';
import { ProjectBadge, StageBadge, InvoiceBadge } from '../components/Badge';
import ProgressBar from '../components/ProgressBar';
import ClickUpProfitCard from '../components/ClickUpProfitCard';
import BudgetSpent from '../components/BudgetSpent';
import { useClickUpStatus } from '../lib/clickupStatus';
import Modal from '../components/Modal';
import type { Stage, InvoiceMilestone, StageStatus, InvoiceStatus, ProjectStatus, Project } from '../types';

// ── Stage / invoice generation (shared with Projects page) ───────────────────
function buildStages(projectId: string, startDate: string, endDate: string) {
  const toStr = (d: Date) => d.toISOString().split('T')[0];
  const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
  const start = new Date(startDate);
  const end = new Date(endDate);
  const total = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
  const splits = [0.10, 0.25, 0.50, 0.15];
  const templates = [
    { title: 'Projekti käivitamine & materjalide kogumine', description: 'Projekti seadistamine, kliendi materjalide, sisu ja brändiraamatu kogumine.' },
    { title: 'Disain',                                      description: 'UX/UI kujundus. Korrektuurivoorud kliendiga.' },
    { title: 'Arendustöö',                                  description: 'Frontend ja backend arendus, integratsioonid.' },
    { title: 'Testimine & käivitamine',                     description: 'QA testimine, SEO seadistus, live keskkonda viimine, kliendi koolitus.' },
  ];
  const stages = [];
  let cursor = new Date(start);
  for (let i = 0; i < templates.length; i++) {
    const days = Math.round(total * splits[i]);
    const stageEnd = i === templates.length - 1 ? end : addDays(cursor, days - 1);
    stages.push({
      projectId, title: templates[i].title, description: templates[i].description,
      order: i + 1, plannedStart: toStr(cursor), plannedEnd: toStr(stageEnd),
      completionPercent: 0, status: 'not_started' as const, notes: '', blockerNote: '',
    });
    cursor = addDays(stageEnd, 1);
  }
  return stages;
}

type InvoiceFrequency = 'monthly' | 'quarterly' | 'stage_start';

// Contract-driven invoice builder
function buildInvoices(params: {
  projectId: string;
  stageIds: string[];
  stageStartDates: string[];
  stageEndDates: string[];
  startDate: string;
  count: number;
  amountPerInvoice: number;
  frequency: InvoiceFrequency;
  bonusAmount: number;
}) {
  const { projectId, stageIds, stageStartDates, stageEndDates, startDate, count, amountPerInvoice, frequency, bonusAmount } = params;
  const toStr = (d: Date) => d.toISOString().split('T')[0];
  const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, d.getDate());

  const result: {
    projectId: string; relatedStageId: string; title: string;
    amount: number; percentage: number; plannedSendDate: string;
    sentDate: string; paidDate: string; status: 'not_ready'; notes: string;
  }[] = [];

  const total = count + (bonusAmount > 0 ? 1 : 0);
  const totalRegular = count * amountPerInvoice;

  for (let i = 0; i < count; i++) {
    let date: string;
    if (frequency === 'stage_start') {
      date = stageStartDates[Math.min(i, stageStartDates.length - 1)] ?? startDate;
    } else {
      const intervalMonths = frequency === 'quarterly' ? 3 : 1;
      date = toStr(addMonths(new Date(startDate), i * intervalMonths));
    }
    const pct = Math.round((amountPerInvoice / (totalRegular + bonusAmount)) * 100);
    const label = frequency === 'stage_start'
      ? `Etapp ${i + 1} algus`
      : frequency === 'monthly' ? `${i + 1}. kuu` : `${i + 1}. kvartal`;
    result.push({
      projectId,
      relatedStageId: stageIds[Math.min(i, stageIds.length - 1)] ?? '',
      title: `Arve ${i + 1}/${total} — ${label}`,
      amount: amountPerInvoice,
      percentage: pct,
      plannedSendDate: date,
      sentDate: '', paidDate: '', status: 'not_ready', notes: '',
    });
  }

  if (bonusAmount > 0) {
    const bonusDate = stageEndDates[stageEndDates.length - 1]
      ?? toStr(addMonths(new Date(startDate), count));
    result.push({
      projectId,
      relatedStageId: stageIds[stageIds.length - 1] ?? '',
      title: `Arve ${total}/${total} — eelarve ületamine`,
      amount: bonusAmount,
      percentage: Math.round((bonusAmount / (totalRegular + bonusAmount)) * 100),
      plannedSendDate: bonusDate,
      sentDate: '', paidDate: '', status: 'not_ready', notes: '',
    });
  }

  return result;
}

// Compute project end date when using monthly/quarterly schedule
function computeEndDate(startDate: string, count: number, frequency: InvoiceFrequency): string {
  if (!startDate || frequency === 'stage_start') return '';
  const intervalMonths = frequency === 'quarterly' ? 3 : 1;
  const d = new Date(startDate);
  const end = new Date(d.getFullYear(), d.getMonth() + (count - 1) * intervalMonths + intervalMonths - 1, d.getDate());
  return end.toISOString().split('T')[0];
}

type Tab = 'overview' | 'stages' | 'documents' | 'invoices';

// ────────────────────────────────────────────────
// Stage row (inline editable)
// ────────────────────────────────────────────────
function StageRow({
  stage,
  invoices,
  onUpdate,
  onDelete,
}: {
  stage: Stage;
  invoices: InvoiceMilestone[];
  onUpdate: (updates: Partial<Stage>) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    ...stage,
    plannedStart: stage.plannedStart?.split('T')[0] ?? '',
    plannedEnd: stage.plannedEnd?.split('T')[0] ?? '',
  });
  const linked = invoices.find((i) => i.relatedStageId === stage.id);

  const inputCls =
    'px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500';

  if (editing) {
    return (
      <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Title</label>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Start</label>
              <input type="date" value={form.plannedStart} onChange={(e) => setForm((f) => ({ ...f, plannedStart: e.target.value }))} className={`${inputCls} w-full`} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">End</label>
              <input type="date" value={form.plannedEnd} onChange={(e) => setForm((f) => ({ ...f, plannedEnd: e.target.value }))} className={`${inputCls} w-full`} />
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Description</label>
          <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} w-full resize-none`} rows={2} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as StageStatus }))} className={`${inputCls} w-full`}>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Completion %</label>
            <input type="number" min={0} max={100} value={form.completionPercent} onChange={(e) => setForm((f) => ({ ...f, completionPercent: Number(e.target.value) }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Notes</label>
            <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
        </div>
        {form.status === 'blocked' && (
          <div>
            <label className="text-xs text-gray-500 block mb-1">Blocker description</label>
            <input value={form.blockerNote} onChange={(e) => setForm((f) => ({ ...f, blockerNote: e.target.value }))} className={`${inputCls} w-full`} placeholder="What is blocking this stage?" />
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={() => setEditing(false)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            <X size={12} /> Cancel
          </button>
          <button onClick={() => { onUpdate(form); setEditing(false); }} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600">
            <Save size={12} /> Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 group ${stage.status === 'blocked' ? 'border-red-200 bg-red-50/40' : 'border-gray-100 bg-white'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${stage.status === 'completed' ? 'bg-green-100 text-green-700' : stage.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : stage.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
          {stage.order}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 text-sm">{stage.title}</span>
              <StageBadge status={stage.status} />
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              <button onClick={() => setEditing(true)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Pencil size={12} />
              </button>
              <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          {stage.description && (
            <p className="text-xs text-gray-500 mt-1">{stage.description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            <span className="text-xs text-gray-400">
              {formatDate(stage.plannedStart)} → {formatDate(stage.plannedEnd)}
            </span>
            {linked && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Receipt size={11} className="text-gray-400" />
                <InvoiceBadge status={linked.status} />
                {formatCurrency(linked.amount)}
              </span>
            )}
          </div>
          {stage.status !== 'not_started' && (
            <div className="mt-2.5">
              <ProgressBar value={stage.completionPercent} showLabel />
            </div>
          )}
          {stage.status === 'blocked' && stage.blockerNote && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {stage.blockerNote}
            </div>
          )}
          {stage.notes && stage.status !== 'blocked' && (
            <p className="mt-1.5 text-xs text-gray-400 italic">{stage.notes}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Add Stage form
// ────────────────────────────────────────────────
function AddStageForm({ projectId, nextOrder, defaultStart, onSave, onCancel }: {
  projectId: string; nextOrder: number;
  defaultStart?: string;
  onSave: (s: Omit<Stage, 'id'>) => void;
  onCancel: () => void;
}) {
  const addDays = (dateStr: string, n: number) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
  };
  const initialStart = defaultStart || new Date().toISOString().split('T')[0];
  const [form, setForm] = useState<Omit<Stage, 'id'>>({
    projectId,
    title: '',
    description: '',
    order: nextOrder,
    plannedStart: initialStart,
    plannedEnd: addDays(initialStart, 14),
    completionPercent: 0,
    status: 'not_started',
    notes: '',
    blockerNote: '',
  });
  const canSave = form.title.trim().length > 0 && !!form.plannedStart && !!form.plannedEnd;
  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Stage Title *</label>
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="e.g. UX Design" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Start</label>
            <input type="date" value={form.plannedStart} onChange={(e) => setForm((f) => ({ ...f, plannedStart: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">End</label>
            <input type="date" value={form.plannedEnd} onChange={(e) => setForm((f) => ({ ...f, plannedEnd: e.target.value }))} className={inputCls} />
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Description</label>
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} rows={2} />
      </div>
      <div className="flex items-center justify-end gap-2">
        {!canSave && <span className="text-xs text-gray-400 mr-auto">Title, start and end date are required</span>}
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={() => canSave && onSave(form)} disabled={!canSave} className="px-3 py-1.5 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50">Add Stage</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Document upload panel
// ────────────────────────────────────────────────
function DocumentsTab({ project }: { project: Project }) {
  const projectId = project.id;
  const documents = useStore((s) => s.documents);
  const stages    = useStore((s) => s.stages);
  const invoices  = useStore((s) => s.invoices);
  const addDocument    = useStore((s) => s.addDocument);
  const deleteDocument = useStore((s) => s.deleteDocument);
  const deleteStage    = useStore((s) => s.deleteStage);
  const deleteInvoice  = useStore((s) => s.deleteInvoice);
  const addStage       = useStore((s) => s.addStage);
  const addInvoice     = useStore((s) => s.addInvoice);
  const updateProject  = useStore((s) => s.updateProject);
  const currentUser    = useStore((s) => s.currentUser);

  const projectDocs     = documents.filter((d) => d.projectId === projectId);
  const projectStages   = stages.filter((s) => s.projectId === projectId);

  // Modal state
  const [showModal, setShowModal]           = useState(false);
  const [parsing, setParsing]               = useState(false);
  const [parseError, setParseError]         = useState('');
  const [parsed, setParsed]                 = useState<ContractParseResult | null>(null);

  // Editable fields (pre-filled by parser)
  const [startDate, setStartDate]           = useState(project.startDate);
  const [invoiceCount, setInvoiceCount]     = useState(2);
  const [frequency, setFrequency]           = useState<InvoiceFrequency>('monthly');
  const [amountPerInvoice, setAmountPerInvoice] = useState(
    project.value > 0 ? Math.round(project.value / 2) : 0
  );
  const [hasBonus, setHasBonus]             = useState(false);
  const [bonusAmount, setBonusAmount]       = useState(0);
  // Stages from contract (overrides generic generation when present)
  const [contractStages, setContractStages] = useState<ContractParseResult['stages']>([]);

  const [regenerating, setRegenerating]     = useState(false);
  const [done, setDone]                     = useState(false);

  // End date: from last contract stage, or computed from invoice schedule
  const derivedEndDate = (() => {
    if (contractStages.length > 0) return contractStages[contractStages.length - 1].plannedEnd;
    return computeEndDate(startDate, invoiceCount, frequency);
  })();

  const applyParsed = (p: ContractParseResult) => {
    setParsed(p);
    if (p.stages.length > 0) {
      setStartDate(p.stages[0].plannedStart);
      setContractStages(p.stages);
    } else if (p.stages.length === 0 && startDate) {
      setContractStages([]);
    }
    if (p.invoiceCount) setInvoiceCount(p.invoiceCount);
    if (p.amountPerInvoice) setAmountPerInvoice(p.amountPerInvoice);
    if (p.invoiceFrequency) setFrequency(p.invoiceFrequency);
    if (p.bonusAmount && p.bonusAmount > 0) { setHasBonus(true); setBonusAmount(p.bonusAmount); }
  };

  const openModal = (prefill?: ContractParseResult) => {
    if (prefill) applyParsed(prefill);
    setDone(false);
    setShowModal(true);
  };

  const [changeLog, setChangeLog] = useState<string[]>([]);

  const applySchedule = (
    resolvedStages: ContractParseResult['stages'],
    count: number, amt: number, freq: InvoiceFrequency, bonus: number,
    resolvedStart: string, resolvedEnd: string,
  ) => {
    // Snapshot old state for change summary
    const oldStages   = useStore.getState().stages.filter((s) => s.projectId === projectId);
    const oldInvoices = useStore.getState().invoices.filter((i) => i.projectId === projectId);
    const oldStart    = project.startDate;
    const oldEnd      = project.endDate;

    // Build change log
    const changes: string[] = [];
    if (resolvedStart !== oldStart || resolvedEnd !== oldEnd) {
      changes.push(`Timeline: ${formatDate(oldStart) || '—'} → ${formatDate(oldEnd) || '—'}  changed to  ${formatDate(resolvedStart)} → ${formatDate(resolvedEnd)}`);
    }
    if (resolvedStages.length > 0 && resolvedStages.length !== oldStages.length) {
      changes.push(`Stages: ${oldStages.length} → ${resolvedStages.length} (${resolvedStages.map(s => s.title).join(', ')})`);
    } else if (resolvedStages.length > 0) {
      changes.push(`Stages replaced: ${resolvedStages.map(s => s.title).join(', ')}`);
    } else {
      changes.push(`Stages regenerated proportionally (${count} invoice periods)`);
    }
    const newInvCount = count + (bonus > 0 ? 1 : 0);
    changes.push(`Invoices: ${oldInvoices.length} → ${newInvCount} · ${formatCurrency(amt, project.currency)} each${bonus > 0 ? ` + ${formatCurrency(bonus, project.currency)} overrun` : ''}`);

    // Update project dates
    updateProject(projectId, { startDate: resolvedStart, endDate: resolvedEnd });

    // Remove ALL existing stages + invoices
    oldStages.forEach((s) => deleteStage(s.id));
    oldInvoices.forEach((i) => deleteInvoice(i.id));

    // Add new stages
    if (resolvedStages.length > 0) {
      resolvedStages.forEach((cs, i) => addStage({
        projectId, title: cs.title, description: '',
        order: i + 1, plannedStart: cs.plannedStart, plannedEnd: cs.plannedEnd,
        completionPercent: 0, status: 'not_started', notes: '', blockerNote: '',
      }));
    } else {
      buildStages(projectId, resolvedStart, resolvedEnd).forEach((s) => addStage(s));
    }

    // Add new invoices
    setTimeout(() => {
      const liveStages = useStore.getState().stages
        .filter((s) => s.projectId === projectId)
        .sort((a, b) => a.order - b.order);
      buildInvoices({
        projectId,
        stageIds:        liveStages.map((s) => s.id),
        stageStartDates: liveStages.map((s) => s.plannedStart),
        stageEndDates:   liveStages.map((s) => s.plannedEnd),
        startDate: resolvedStart,
        count, amountPerInvoice: amt, frequency: freq, bonusAmount: bonus,
      }).forEach((inv) => addInvoice(inv));
      setChangeLog(changes);
      setDone(true);
    }, 0);
  };

  const autoApply = (p: ContractParseResult) => {
    const resolvedStages = p.stages;
    const resolvedStart  = resolvedStages[0]?.plannedStart ?? project.startDate ?? new Date().toISOString().split('T')[0];
    const resolvedEnd    = resolvedStages[resolvedStages.length - 1]?.plannedEnd ?? resolvedStart;
    const count          = p.invoiceCount ?? invoiceCount;
    const amt            = p.amountPerInvoice ?? amountPerInvoice;
    const freq           = p.invoiceFrequency ?? frequency;
    const bonus          = p.bonusAmount ?? 0;

    // Sync modal state
    setStartDate(resolvedStart);
    setInvoiceCount(count);
    setAmountPerInvoice(amt);
    setFrequency(freq);
    setHasBonus(bonus > 0);
    setBonusAmount(bonus);
    setContractStages(resolvedStages);
    setParsed(p);

    applySchedule(resolvedStages, count, amt, freq, bonus, resolvedStart, resolvedEnd);
  };

  const [aiDocId, setAiDocId] = useState<string | null>(null);
  const [showAiModal, setShowAiModal] = useState(false);

  const handleUpload = (type: 'offer' | 'contract') => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setDone(false);
    setChangeLog([]);

    const name = file.name.toLowerCase();
    const isSupported = name.endsWith('.pdf') || name.endsWith('.docx');
    if (isSupported) {
      setParsing(true);
      setParseError('');
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('projectId', projectId);
        fd.append('type', type);
        const uploadResp = await apiClient.upload<{ document: { id: string } }>('/documents', fd);
        const docId = uploadResp.document.id;
        await useStore.getState().refetchProjects();

        // Open AI review
        setAiDocId(docId);
        setShowAiModal(true);
        setParsing(false);
      } catch (err) {
        setParsing(false);
        const msg = err instanceof Error ? err.message : '';
        setParseError(msg || 'Upload failed. Please try again.');
      }
    } else {
      setParseError('Only PDF and DOCX files are supported.');
    }
  };

  // Manual apply — called from the modal after user edits
  const handleRegenerate = () => {
    setRegenerating(true);
    const toDateStr = (d: Date) => d.toISOString().split('T')[0];
    const resolvedStart = startDate || toDateStr(new Date());
    const resolvedEnd   = derivedEndDate || toDateStr(new Date(Date.now() + 90 * 86400000));
    applySchedule(
      contractStages, invoiceCount, amountPerInvoice, frequency,
      hasBonus ? bonusAmount : 0, resolvedStart, resolvedEnd,
    );
    setTimeout(() => { setRegenerating(false); setShowModal(false); }, 10);
  };

  return (
    <div className="space-y-6">
      {/* Upload zones */}
      <div className="grid grid-cols-2 gap-4">
        {(['offer', 'contract'] as const).map((type) => {
          const doc = [...projectDocs].filter((d) => d.type === type && d.isLatest !== false).sort((a, b) => b.version - a.version)[0];
          return (
            <div key={type} className="border-2 border-dashed border-gray-200 rounded-xl p-5 hover:border-brand-400 transition-colors bg-white">
              <div className="text-center">
                <File size={28} className={`mx-auto mb-2 ${doc ? 'text-brand-500' : 'text-gray-300'}`} />
                <p className="text-sm font-medium text-gray-700 capitalize mb-1">{type}</p>
                {doc ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 truncate">{doc.fileName}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(doc.fileSize)} · v{doc.version} · {formatDate(doc.uploadDate)}</p>
                    <div className="flex gap-2 justify-center flex-wrap">
                      <a
                        href={`/api/documents/${doc.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-brand-500 hover:text-brand-700 font-medium flex items-center gap-1"
                      >
                        <Eye size={11} /> View
                      </a>
                      <label className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 font-medium">
                        Replace
                        <input type="file" className="hidden" accept=".pdf,.docx" onChange={handleUpload(type)} />
                      </label>
                      <button onClick={() => deleteDocument(doc.id)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs bg-brand-50 text-brand-600 rounded-lg hover:bg-brand-100 transition-colors font-medium">
                      <Upload size={12} /> Upload {type}
                    </span>
                    <input type="file" className="hidden" accept=".pdf,.docx" onChange={handleUpload(type)} />
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Uploaded documents list */}
      {projectDocs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-5 py-3 border-b border-gray-50">
            <h3 className="text-sm font-medium text-gray-700">Uploaded Documents</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {[...projectDocs].sort((a, b) => b.version - a.version).map((doc) => {
              const isLatest = doc.isLatest !== false;
              return (
                <div key={doc.id} className={`flex items-center justify-between px-5 py-3 group ${!isLatest ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`px-2 py-0.5 rounded text-xs font-medium ${doc.type === 'offer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {doc.type}
                    </div>
                    {!isLatest && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-400">archived</span>
                    )}
                    <div>
                      <p className="text-sm text-gray-800">{doc.fileName}</p>
                      <p className="text-xs text-gray-400">{formatFileSize(doc.fileSize)} · v{doc.version} · {formatDate(doc.uploadDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <a
                      href={`/api/documents/${doc.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-gray-400 hover:text-brand-600 rounded-lg hover:bg-brand-50"
                      title={doc.mimeType === 'application/pdf' ? 'View PDF' : 'Download'}
                    >
                      {doc.mimeType !== 'application/pdf' ? <Download size={13} /> : <Eye size={13} />}
                    </a>
                    {isLatest && (
                      <button
                        onClick={() => { setAiDocId(doc.id); setShowAiModal(true); }}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-brand-600 hover:bg-brand-50 rounded-lg font-medium"
                        title="Extract data with AI"
                      >
                        <Sparkles size={12} /> Extract with AI
                      </button>
                    )}
                    <button onClick={() => deleteDocument(doc.id)} className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Parsing spinner */}
      {parsing && (
        <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border border-brand-100 rounded-xl">
          <svg className="animate-spin h-4 w-4 text-brand-500 shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-sm text-brand-700">Reading contract… extracting billing terms and stages.</p>
        </div>
      )}
      {parseError && (
        <div className="px-4 py-3 bg-yellow-50 border border-yellow-100 rounded-xl text-sm text-yellow-700">{parseError}</div>
      )}

      {/* Manual regenerate */}
      {(projectStages.length > 0 || projectDocs.length > 0) && !parsing && (
        <button
          onClick={() => openModal()}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors"
        >
          <RefreshCw size={14} /> Update schedule & regenerate stages / invoices
        </button>
      )}

      {done && (
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle2 size={18} className="text-green-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800 mb-1">Project updated from document</p>
            {changeLog.length > 0 && (
              <ul className="space-y-0.5">
                {changeLog.map((line, i) => (
                  <li key={i} className="text-xs text-green-700 flex items-start gap-1.5">
                    <span className="text-green-400 mt-0.5 shrink-0">→</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button onClick={() => openModal()} className="text-xs text-green-700 underline shrink-0 hover:text-green-900 mt-0.5">
            Adjust
          </button>
        </div>
      )}

      {/* Contract schedule modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Invoice schedule from contract" size="lg">
        <div className="space-y-5">

          {/* Extracted banner */}
          {parsed && (parsed.invoiceCount || parsed.stages.length > 0) && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-brand-50 border border-brand-100 rounded-xl text-xs text-brand-700">
              <Sparkles size={14} className="shrink-0 mt-0.5" />
              <span>
                Extracted from PDF: {[
                  parsed.invoiceCount && `${parsed.invoiceCount} invoices`,
                  parsed.amountPerInvoice && `${formatCurrency(parsed.amountPerInvoice, project.currency)} each`,
                  parsed.bonusAmount && `+${formatCurrency(parsed.bonusAmount, project.currency)} overrun`,
                  parsed.stages.length > 0 && `${parsed.stages.length} stages`,
                ].filter(Boolean).join(' · ')}. Review and adjust below.
              </span>
            </div>
          )}

          {/* Start date */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Project Start</p>
            <input type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-48 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>

          {/* Stages from contract */}
          {contractStages.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Stages from contract <span className="text-gray-400 font-normal normal-case">(§1.3)</span></p>
                <button onClick={() => setContractStages([])} className="text-xs text-gray-400 hover:text-red-500">Clear — use generic stages</button>
              </div>
              {contractStages.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-lg border border-gray-100 px-3 py-2">
                  <span className="w-5 h-5 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="text-xs text-gray-700 flex-1 truncate">{s.title}</span>
                  <span className="text-xs text-gray-400 shrink-0">{formatDate(s.plannedStart)} → {formatDate(s.plannedEnd)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Regular invoices §4.1.1 */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Regular invoices <span className="text-gray-400 font-normal normal-case">(§4.1.1)</span></p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Number of invoices</label>
                <input type="number" min={1} max={60} value={invoiceCount}
                  onChange={(e) => setInvoiceCount(Math.max(1, Number(e.target.value)))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 font-medium" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Payment timing</label>
                <div className="flex gap-2">
                  {([
                    { val: 'stage_start', label: 'Per stage' },
                    { val: 'monthly',     label: 'Monthly' },
                    { val: 'quarterly',   label: 'Quarterly' },
                  ] as { val: InvoiceFrequency; label: string }[]).map(({ val, label }) => (
                    <button key={val} onClick={() => setFrequency(val)}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${frequency === val ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Amount per invoice (EUR excl. VAT)</label>
              <div className="flex items-center gap-3">
                <input type="number" min={0} value={amountPerInvoice}
                  onChange={(e) => setAmountPerInvoice(Math.max(0, Number(e.target.value)))}
                  className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 font-medium" />
                <span className="text-xs text-gray-400">Total: {formatCurrency(amountPerInvoice * invoiceCount, project.currency)}</span>
              </div>
            </div>
          </div>

          {/* Optional overrun §4.1.2 */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input type="checkbox" id="hasBonus" checked={hasBonus}
                onChange={(e) => setHasBonus(e.target.checked)}
                className="w-4 h-4 accent-brand-500 cursor-pointer" />
              <label htmlFor="hasBonus" className="text-sm font-medium text-gray-700 cursor-pointer">
                Additional invoice for budget overrun <span className="text-gray-400 font-normal">(§4.1.2)</span>
              </label>
            </div>
            {hasBonus && (
              <div className="flex items-center gap-3 pl-7">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Max amount (EUR excl. VAT)</label>
                  <input type="number" min={0} value={bonusAmount}
                    onChange={(e) => setBonusAmount(Math.max(0, Number(e.target.value)))}
                    className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 font-medium" />
                </div>
                <p className="text-xs text-gray-400 mt-4">Sent after final stage if min budget is exceeded.</p>
              </div>
            )}
          </div>

          {/* Summary */}
          {startDate && (
            <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Project end</p>
                <p className="text-sm font-bold text-gray-800">{formatDate(derivedEndDate) || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Total invoices</p>
                <p className="text-sm font-bold text-gray-800">{invoiceCount + (hasBonus && bonusAmount > 0 ? 1 : 0)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Total project value</p>
                <p className="text-sm font-bold text-gray-800">{formatCurrency(amountPerInvoice * invoiceCount + (hasBonus ? bonusAmount : 0), project.currency)}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Skip for now
            </button>
            <button onClick={handleRegenerate} disabled={regenerating || !startDate}
              className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-60 flex items-center gap-2">
              {regenerating
                ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Regenerating…</>
                : <><RefreshCw size={14} /> Apply & Regenerate</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* AI extraction modal */}
      {aiDocId && (
        <AiReviewModal
          open={showAiModal}
          documentId={aiDocId}
          projectName={project.name}
          currentProject={project}
          onClose={() => { setShowAiModal(false); setAiDocId(null); }}
          onApplied={async () => {
            await useStore.getState().refetchProjects();
            setDone(true);
            setChangeLog(['Project data updated from AI extraction']);
          }}
        />
      )}
    </div>
  );
}

// Server requires a full URL — quietly prepend https:// for pasted bare domains.
function normalizeUrl(url: string | null | undefined): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

// ────────────────────────────────────────────────
// Drive link button — opens link or lets you set/clear it
// ────────────────────────────────────────────────
function DriveLinkButton({ projectId, driveLink, onSave }: { projectId: string; driveLink: string | null; onSave: (url: string | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(driveLink ?? '');
  void projectId;

  const commit = () => {
    onSave(normalizeUrl(local));
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="url"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="https://drive.google.com/..."
          className="px-2 py-1.5 text-xs border border-brand-400 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-400 w-56"
        />
        <button onClick={commit} className="px-2 py-1.5 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium">Save</button>
        <button onClick={() => setEditing(false)} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    );
  }

  if (driveLink) {
    return (
      <div className="flex items-center gap-1">
        <a
          href={driveLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium transition-colors border border-green-200"
        >
          <svg viewBox="0 0 87.3 78" className="w-3 h-3 fill-current" xmlns="http://www.w3.org/2000/svg"><path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/><path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/><path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/><path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/><path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/><path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/></svg>
          Drive
        </a>
        <button onClick={() => { setLocal(driveLink); setEditing(true); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" title="Edit Drive link">
          <Pencil size={11} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setLocal(''); setEditing(true); }}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 font-medium transition-colors"
    >
      <svg viewBox="0 0 87.3 78" className="w-3 h-3 fill-current opacity-60" xmlns="http://www.w3.org/2000/svg"><path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/><path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"/><path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"/><path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/><path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/><path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/></svg>
      Add Drive link
    </button>
  );
}

// ────────────────────────────────────────────────
// Completion % input — saves only on blur / Enter
// ────────────────────────────────────────────────
function CompletionInput({ projectId, value, onSave }: { projectId: string; value: number; onSave: (v: number) => void }) {
  const [local, setLocal] = useState(String(value));
  // Keep in sync if the value changes externally (e.g. SSE refetch)
  const prevValue = useRef(value);
  if (prevValue.current !== value) {
    prevValue.current = value;
    setLocal(String(value));
  }
  const commit = () => {
    const n = Math.min(100, Math.max(0, Number(local) || 0));
    setLocal(String(n));
    if (n !== value) onSave(n);
  };
  void projectId;
  return (
    <div className="flex items-center gap-2 mt-1">
      <ProgressBar value={value} size="md" />
      <input
        type="number"
        min={0}
        max={100}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
        className="w-14 text-sm font-bold text-gray-800 text-center border border-transparent rounded-lg hover:border-gray-200 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400 px-1 py-0.5"
        title="Edit completion %"
      />
      <span className="text-sm font-bold text-gray-500 -ml-2">%</span>
    </div>
  );
}

// ────────────────────────────────────────────────
// Edit Project form (modal)
// ────────────────────────────────────────────────
function EditProjectForm({
  project,
  onSave,
  onCancel,
}: {
  project: { id: string; clientId: string; name: string; description: string; value: number; currency: string; salesOwnerId: string; projectManagerId: string; startDate: string; endDate: string; status: ProjectStatus; driveLink?: string | null };
  onSave: (updates: Partial<typeof project>) => void;
  onCancel: () => void;
}) {
  const users = useStore((s) => s.users);
  const clients = useStore((s) => s.clients);
  const [form, setForm] = useState({
    ...project,
    startDate: project.startDate ? project.startDate.slice(0, 10) : '',
    endDate: project.endDate ? project.endDate.slice(0, 10) : '',
  });
  const salesUsers = users.filter((u) => ['sales', 'sales_pm', 'admin', 'super_admin'].includes(u.role));
  const pmUsers = users.filter((u) => ['pm', 'sales_pm', 'admin', 'super_admin'].includes(u.role));
  const sortedClients = [...clients].sort((a, b) => a.companyName.localeCompare(b.companyName));
  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Project Name *</label>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Client *</label>
        <select value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))} className={inputCls}>
          {sortedClients.map((c) => (
            <option key={c.id} value={c.id}>{c.companyName}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Description</label>
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} rows={2} placeholder="Short project description..." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Value</label>
          <input type="number" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: Number(e.target.value) }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Currency</label>
          <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className={inputCls}>
            <option>EUR</option><option>USD</option><option>GBP</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Sales Owner</label>
          <select value={form.salesOwnerId} onChange={(e) => setForm((f) => ({ ...f, salesOwnerId: e.target.value }))} className={inputCls}>
            <option value="">None</option>
            {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Project Manager</label>
          <select value={form.projectManagerId} onChange={(e) => setForm((f) => ({ ...f, projectManagerId: e.target.value }))} className={inputCls}>
            <option value="">None</option>
            {pmUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Start Date</label>
          <input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">End Date</label>
          <input type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} className={inputCls} />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Status</label>
        <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ProjectStatus }))} className={inputCls}>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Google Drive link</label>
        <input
          type="url"
          value={form.driveLink ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, driveLink: e.target.value || null }))}
          className={inputCls}
          placeholder="https://drive.google.com/..."
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button
          onClick={() => form.name.trim() && onSave(form)}
          className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Main ProjectDetail page
// ────────────────────────────────────────────────
export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const users = useStore((s) => s.users);
  const stages = useStore((s) => s.stages);
  const invoices = useStore((s) => s.invoices);
  const messages = useStore((s) => s.messages);
  const updateStage = useStore((s) => s.updateStage);
  const deleteStage = useStore((s) => s.deleteStage);
  const addStage = useStore((s) => s.addStage);
  const updateProject = useStore((s) => s.updateProject);
  const deleteProject = useStore((s) => s.deleteProject);
  const updateInvoice = useStore((s) => s.updateInvoice);
  const deleteInvoice = useStore((s) => s.deleteInvoice);
  const addInvoice = useStore((s) => s.addInvoice);

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [addingStage, setAddingStage] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { status: clickup } = useClickUpStatus();

  const project = projects.find((p) => p.id === projectId);
  const client = clients.find((c) => c.id === project?.clientId);
  const pm = users.find((u) => u.id === project?.projectManagerId);
  const sales = users.find((u) => u.id === project?.salesOwnerId);
  const projectStages = stages.filter((s) => s.projectId === projectId).sort((a, b) => a.order - b.order);
  const projectInvoices = invoices.filter((i) => i.projectId === projectId);
  const projectMessages = messages.filter((m) => m.projectId === projectId);
  const openQuestions = projectMessages.filter((m) => m.isQuestion && !m.resolved);

  if (!project) {
    return (
      <div className="p-8 text-center text-gray-400">
        Project not found. <button onClick={() => navigate('/projects')} className="text-brand-500">Go back</button>
      </div>
    );
  }

  const handleStageUpdate = (stageId: string, updates: Partial<Stage>) => {
    updateStage(stageId, updates);
  };

  const handleStageDelete = (stageId: string) => {
    deleteStage(stageId);
  };

  const handleStageAdd = (s: Omit<Stage, 'id'>) => {
    addStage(s);
    setAddingStage(false);
  };

  const TABS = [
    { id: 'overview' as Tab, label: 'Overview', icon: BarChart3 },
    { id: 'stages' as Tab, label: `Stages (${projectStages.length})`, icon: Layers },
    { id: 'documents' as Tab, label: 'Documents', icon: FileText },
    { id: 'invoices' as Tab, label: `Invoices (${projectInvoices.length})`, icon: Receipt },
  ];

  const paidAmount = projectInvoices.filter((i) => i.status === 'paid').reduce((s, i) => s + Number(i.amount), 0);
  const pendingAmount = projectInvoices.filter((i) => i.status !== 'paid').reduce((s, i) => s + Number(i.amount), 0);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Back */}
      <button onClick={() => navigate('/projects')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft size={14} /> Projects
      </button>

      {/* Project header card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
              <ProjectBadge status={project.status} />
            </div>
            <p className="text-gray-400 text-sm">{client?.companyName}</p>
          </div>
          <div className="flex items-start gap-3 shrink-0">
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(project.value, project.currency)}</div>
              <div className="text-xs text-gray-400 mt-0.5">Total project value</div>
            </div>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setEditingProject(true)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Edit project"
              >
                <Settings size={14} />
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete project"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Sales Owner</p>
            <p className="text-sm font-medium text-gray-700">{sales?.name ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Project Manager</p>
            <p className="text-sm font-medium text-gray-700">{pm?.name ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Timeline</p>
            <p className="text-sm font-medium text-gray-700">{formatDate(project.startDate)} → {formatDate(project.endDate)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Completion</p>
            <CompletionInput projectId={project.id} value={project.overallCompletion} onSave={(v) => updateProject(project.id, { overallCompletion: v })} />
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Budget spent</p>
            <div className="mt-1.5">
              <BudgetSpent project={project} hourlyCost={clickup?.hourlyCost ?? null} detailed />
            </div>
          </div>
        </div>

        {/* Quick action buttons */}
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => navigate(`/projects/${project.id}/timeline`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
          >
            <Calendar size={13} /> Timeline
          </button>
          <button
            onClick={() => navigate(`/projects/${project.id}/invoices`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
          >
            <Receipt size={13} /> Invoices
            {projectInvoices.some((i) => i.status === 'overdue') && (
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            )}
          </button>
          <button
            onClick={() => navigate(`/projects/${project.id}/chat`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
          >
            <MessageCircle size={13} /> Q&A Chat
            {openQuestions.length > 0 && (
              <span className="px-1.5 bg-purple-500 text-white text-xs rounded-full leading-none py-0.5">
                {openQuestions.length}
              </span>
            )}
          </button>
          <DriveLinkButton projectId={project.id} driveLink={project.driveLink ?? null} onSave={(url) => updateProject(project.id, { driveLink: url })} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-white border border-gray-100 rounded-xl p-1.5">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id ? 'bg-brand-500 text-white' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-xs text-gray-400 mb-1">Paid to Date</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(paidAmount, project.currency)}</p>
            <p className="text-xs text-gray-400 mt-1">{Math.round((paidAmount / project.value) * 100)}% of total</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-xs text-gray-400 mb-1">Remaining to Invoice</p>
            <p className="text-2xl font-bold text-gray-800">{formatCurrency(pendingAmount, project.currency)}</p>
            <p className="text-xs text-gray-400 mt-1">{projectInvoices.filter((i) => i.status !== 'paid').length} milestones outstanding</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-xs text-gray-400 mb-1">Stages Complete</p>
            <p className="text-2xl font-bold text-blue-600">
              {projectStages.filter((s) => s.status === 'completed').length}/{projectStages.length}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {projectStages.filter((s) => s.status === 'blocked').length > 0
                ? `${projectStages.filter((s) => s.status === 'blocked').length} blocked`
                : 'No blockers'}
            </p>
          </div>
        </div>
        <ClickUpProfitCard project={project} clientName={client?.companyName ?? ''} />
        </>
      )}

      {activeTab === 'stages' && (
        <div className="space-y-3">
          {projectStages.map((stage) => (
            <StageRow
              key={stage.id}
              stage={stage}
              invoices={projectInvoices}
              onUpdate={(updates) => handleStageUpdate(stage.id, updates)}
              onDelete={() => handleStageDelete(stage.id)}
            />
          ))}
          {addingStage ? (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <AddStageForm
                projectId={project.id}
                nextOrder={projectStages.length + 1}
                defaultStart={projectStages.length > 0 ? projectStages[projectStages.length - 1].plannedEnd?.split('T')[0] : undefined}
                onSave={handleStageAdd}
                onCancel={() => setAddingStage(false)}
              />
            </div>
          ) : (
            <button
              onClick={() => setAddingStage(true)}
              className="w-full border-2 border-dashed border-gray-200 rounded-xl py-4 text-sm text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={15} /> Add Stage
            </button>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <DocumentsTab project={project} />
      )}

      {activeTab === 'invoices' && (
        <div className="space-y-3">
          {/* Payment progress bar */}
          {projectInvoices.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Payment progress</span>
                <span>{Math.round((paidAmount / (project.value || 1)) * 100)}% of total project value received</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="bg-green-400 transition-all" style={{ width: `${(paidAmount / project.value) * 100}%` }} />
                <div className="bg-blue-300 transition-all" style={{ width: `${(projectInvoices.filter(i => i.status === 'sent').reduce((s,i) => s+i.amount,0) / project.value) * 100}%` }} />
              </div>
              <div className="flex gap-4 mt-1.5">
                <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Paid</span>
                <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-2 h-2 rounded-full bg-blue-300 inline-block" /> Sent</span>
                <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-2 h-2 rounded-full bg-gray-200 inline-block" /> Remaining</span>
              </div>
            </div>
          )}

          {/* Invoice rows */}
          {projectInvoices.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
              <Receipt size={32} className="mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No invoice milestones yet.</p>
            </div>
          )}
          {projectInvoices.map((inv) => {
            const stage = projectStages.find(s => s.id === inv.relatedStageId);
            return (
              <div key={inv.id} className={`bg-white rounded-xl border p-4 ${
                inv.status === 'overdue' ? 'border-red-200 bg-red-50/30' :
                inv.status === 'paid' ? 'border-green-100' :
                inv.status === 'ready_to_send' ? 'border-yellow-200' :
                'border-gray-100'
              }`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-medium text-gray-900 text-sm">{inv.title}</span>
                      <InvoiceBadge status={inv.status} />
                      {stage && <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{stage.title}</span>}
                    </div>
                    {/* Dates row */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Planned send</p>
                        <p className={`text-xs font-medium ${inv.status === 'overdue' ? 'text-red-600' : 'text-gray-700'}`}>
                          {inv.plannedSendDate ? formatDate(inv.plannedSendDate) : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Sent</p>
                        <p className="text-xs font-medium text-gray-700">{inv.sentDate ? formatDate(inv.sentDate) : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Paid</p>
                        <p className="text-xs font-medium text-green-600">{inv.paidDate ? formatDate(inv.paidDate) : '—'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(inv.amount, project.currency)}</p>
                    <p className="text-xs text-gray-400">{shareOfProjectValue(Number(inv.amount), Number(project.value))}%</p>
                  </div>
                </div>
                {/* Quick actions */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                  <div className="flex gap-2 flex-wrap">
                    {inv.status === 'not_ready' && (
                      <button onClick={() => updateInvoice(inv.id, { status: 'ready_to_send' })} className="px-2.5 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 font-medium">Mark Ready</button>
                    )}
                    {inv.status === 'ready_to_send' && (
                      <button onClick={() => updateInvoice(inv.id, { status: 'sent', sentDate: new Date().toISOString().split('T')[0] })} className="px-2.5 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium">Mark Sent</button>
                    )}
                    {inv.status === 'sent' && (
                      <button onClick={() => updateInvoice(inv.id, { status: 'paid', paidDate: new Date().toISOString().split('T')[0] })} className="px-2.5 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium flex items-center gap-1"><CheckCircle2 size={11} /> Mark Paid</button>
                    )}
                    {(inv.status === 'sent' || inv.status === 'ready_to_send') && (
                      <button onClick={() => updateInvoice(inv.id, { status: 'overdue' })} className="px-2.5 py-1 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium flex items-center gap-1"><AlertTriangle size={11} /> Overdue</button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => navigate(`/projects/${project.id}/invoices`)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Edit in full tracker"><Pencil size={12} /></button>
                    <button onClick={() => deleteInvoice(inv.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add milestone */}
          <button
            onClick={() => navigate(`/projects/${project.id}/invoices`)}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl py-3.5 text-sm text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={15} /> Add milestone / manage in full tracker
          </button>
        </div>
      )}

      {/* Edit project modal */}
      <Modal open={editingProject} onClose={() => setEditingProject(false)} title="Edit Project" size="lg">
        <EditProjectForm
          project={project}
          onSave={async (updates) => {
            // Server marks all stages completed + invoices paid when status → completed
            await updateProject(project.id, { ...updates, driveLink: normalizeUrl(updates.driveLink) });
            setEditingProject(false);
          }}
          onCancel={() => setEditingProject(false)}
        />
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete Project">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{project.name}</strong>? This will also remove all stages, invoices, and documents linked to this project.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => { deleteProject(project.id); navigate('/projects'); }}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Delete Project
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
